import type { VolumeProfileSnapshot } from './snapshot';
import type { VolumeProfileWindow } from './window';
import { Instant, ZoneId, ZonedDateTime } from '@js-joda/core';
import type { KiasiHistorySummaryRow, KiasiInterval } from 'src/lib/kiasi';
import { scaledPriceToDollars } from 'src/lib/kiasi/bars';
import type { VolumeBarSnapshot } from 'src/lib/kiasi/bars';

const NEW_YORK = ZoneId.of('America/New_York');
/** Fraction of the price span kept clear above and below the candles. */
const PRICE_PADDING = 0.06;
const AXIS_LABEL_COUNT = 5;

export interface VolumeProfileChartBar {
    readonly id: string;
    readonly label: string;
    /** Top of the wick (the high), as a percentage of the plot height. */
    readonly topPct: number;
    /** Height of the wick, high to low. */
    readonly wickHeightPct: number;
    readonly bodyTopPct: number;
    readonly bodyHeightPct: number;
    /** Bar volume relative to the busiest bar in the chart. */
    readonly volumePct: number;
    readonly direction: 'up' | 'down';
    /** The live open bar, still forming. */
    readonly forming: boolean;
}

export interface VolumeProfileChartAxisLabel {
    readonly label: string;
    readonly topPct: number;
}

export interface VolumeProfileChart {
    readonly bars: readonly VolumeProfileChartBar[];
    /** Price axis labels, top to bottom. */
    readonly axis: readonly VolumeProfileChartAxisLabel[];
    /** Price at the top (0%) and bottom (100%) of the plot, padding included. */
    readonly maxPrice: number;
    readonly minPrice: number;
    readonly pocTopPct: number | null;
    readonly vwapTopPct: number | null;
    readonly lastTopPct: number | null;
    readonly valueArea: { readonly topPct: number; readonly heightPct: number } | null;
    /** Ask and bid shares of side-classified volume, as percentages. */
    readonly askSharePct: number;
    readonly bidSharePct: number;
}

export interface VolumeProfileChartInput {
    readonly rows: readonly KiasiHistorySummaryRow[];
    /** Appended as a forming candle when it is open, newer than every row, and of the chart's interval. */
    readonly liveBar: VolumeBarSnapshot | null;
    readonly interval: KiasiInterval;
    readonly window: VolumeProfileWindow;
    readonly profile: VolumeProfileSnapshot | null;
    /** Draw the profile's POC and value area; off when the selected session traded nothing. */
    readonly keyLevels?: boolean;
    readonly lastPrice: number | null;
}

interface Candle {
    readonly id: string;
    readonly openTimeMs: number;
    readonly open: number;
    readonly high: number;
    readonly low: number;
    readonly close: number;
    readonly volume: number;
    readonly forming: boolean;
}

/** Chart interval for a range: fine enough to show shape, coarse enough to fit a widget. */
export function chartIntervalForRange(range: 'day' | '1h' | '5m' | '1m' | 'custom'): KiasiInterval {
    return range === 'day' || range === 'custom' ? '5m' : '1m';
}

export function formatBarLabel(openTimeMs: number): string {
    const time = ZonedDateTime.ofInstant(Instant.ofEpochMilli(openTimeMs), NEW_YORK);
    const hour12 = time.hour() % 12 === 0 ? 12 : time.hour() % 12;
    const suffix = time.hour() < 12 ? 'a' : 'p';
    const minutes = time.minute();
    return minutes === 0 ? `${hour12}${suffix}` : `${hour12}:${String(minutes).padStart(2, '0')}${suffix}`;
}

function candleFromRow(row: KiasiHistorySummaryRow): Candle | null {
    if (row.open === null || row.high === null || row.low === null || row.close === null) return null;
    const openTimeMs = Math.floor(row.bar_open_ts / 1_000_000);
    return {
        id: String(openTimeMs),
        openTimeMs,
        open: scaledPriceToDollars(row.open),
        high: scaledPriceToDollars(row.high),
        low: scaledPriceToDollars(row.low),
        close: scaledPriceToDollars(row.close),
        volume: row.total_vol,
        forming: false,
    };
}

function candleFromLiveBar(bar: VolumeBarSnapshot): Candle | null {
    if (bar.open === null || bar.high === null || bar.low === null || bar.close === null) return null;
    return {
        id: `${bar.openTimeMs}-live`,
        openTimeMs: bar.openTimeMs,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
        volume: bar.totalVolume,
        forming: true,
    };
}

export function buildChart({
    rows,
    liveBar,
    interval,
    window,
    profile,
    keyLevels = true,
    lastPrice,
}: VolumeProfileChartInput): VolumeProfileChart | null {
    const candles: Candle[] = [];
    for (const row of rows) {
        const candle = candleFromRow(row);
        if (candle !== null) candles.push(candle);
    }
    candles.sort((a, b) => a.openTimeMs - b.openTimeMs);
    const lastOpen = candles.at(-1)?.openTimeMs ?? -Infinity;
    if (
        liveBar !== null &&
        !liveBar.closed &&
        liveBar.interval === interval &&
        liveBar.openTimeMs > lastOpen &&
        liveBar.openTimeMs >= window.fromMs &&
        liveBar.openTimeMs <= window.toMs
    ) {
        const candle = candleFromLiveBar(liveBar);
        if (candle !== null) candles.push(candle);
    }
    if (candles.length === 0) return null;

    const keyPrices = keyLevels ? [profile?.poc, profile?.vah, profile?.val] : [];
    const overlayPrices = [...keyPrices, profile?.vwap, lastPrice].filter(
        (price): price is number => typeof price === 'number' && Number.isFinite(price),
    );
    let minPrice = Math.min(...candles.map((candle) => candle.low), ...overlayPrices);
    let maxPrice = Math.max(...candles.map((candle) => candle.high), ...overlayPrices);
    const span = maxPrice - minPrice;
    const padding = span > 0 ? span * PRICE_PADDING : Math.max(0.01, Math.abs(maxPrice) * 0.001);
    minPrice -= padding;
    maxPrice += padding;
    const range = maxPrice - minPrice;
    const topPct = (price: number): number => ((maxPrice - price) / range) * 100;
    const maxVolume = Math.max(...candles.map((candle) => candle.volume), 1);

    const bars = candles.map(
        (candle): VolumeProfileChartBar => ({
            id: candle.id,
            label: formatBarLabel(candle.openTimeMs),
            topPct: topPct(candle.high),
            wickHeightPct: topPct(candle.low) - topPct(candle.high),
            bodyTopPct: topPct(Math.max(candle.open, candle.close)),
            bodyHeightPct: Math.max(
                0.5,
                topPct(Math.min(candle.open, candle.close)) - topPct(Math.max(candle.open, candle.close)),
            ),
            volumePct: (candle.volume / maxVolume) * 100,
            direction: candle.close >= candle.open ? 'up' : 'down',
            forming: candle.forming,
        }),
    );
    const axis = Array.from({ length: AXIS_LABEL_COUNT }, (_, index): VolumeProfileChartAxisLabel => {
        const price = maxPrice - (range * index) / (AXIS_LABEL_COUNT - 1);
        return {
            label: `$${price.toFixed(Math.abs(price) < 1 ? 4 : 2)}`,
            topPct: (index / (AXIS_LABEL_COUNT - 1)) * 100,
        };
    });
    const sideTotal = profile === null ? 0 : profile.askVolume + profile.bidVolume;
    return {
        bars,
        axis,
        maxPrice,
        minPrice,
        pocTopPct: profile === null || !keyLevels ? null : topPct(profile.poc),
        vwapTopPct: profile?.vwap == null ? null : topPct(profile.vwap),
        lastTopPct: lastPrice === null ? null : topPct(lastPrice),
        valueArea:
            profile === null || !keyLevels
                ? null
                : { topPct: topPct(profile.vah), heightPct: Math.max(0.5, topPct(profile.val) - topPct(profile.vah)) },
        askSharePct: sideTotal === 0 ? 0 : (profile!.askVolume / sideTotal) * 100,
        bidSharePct: sideTotal === 0 ? 0 : (profile!.bidVolume / sideTotal) * 100,
    };
}
